/**
 * Search field of the Home page toolbar, bound to the orchestrator's Algolia
 * query. Rendered by `HomePageToolbar` between the loading spinner and the goal
 * buttons.
 *
 * It is deliberately a non-growing box with a fixed preferred width that
 * shrinks to a 120px floor (`controlHeightLG * 3`) on narrow screens: the group holding the tab
 * bar and the view menu is the single growing element of that row, and a
 * growing search field would swallow the free space and freeze the tab bar into
 * permanent overflow. Below `md` the placeholder is "Search...", because the
 * 375px row leaves the field about 100px.
 */
import { FC, type RefObject } from 'react';
import { Grid, Input, theme, type InputRef } from 'antd';
import { SearchOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { ariaKeyshortcutsFor } from '@/keyboard';

interface HomePageSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  inputRef: RefObject<InputRef | null>;
}

const HomePageSearchBar: FC<HomePageSearchBarProps> = ({
  value,
  onChange,
  inputRef,
}) => {
  const { token } = theme.useToken();
  const { md } = Grid.useBreakpoint();

  return (
    <Input
      id="home-page-search-bar"
      ref={inputRef}
      aria-label="Search forms"
      aria-keyshortcuts={ariaKeyshortcutsFor('global.search')}
      variant="filled"
      placeholder={md ? 'Search forms...' : 'Search...'}
      prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      allowClear={{ clearIcon: <CloseCircleOutlined /> }}
      size="large"
      styles={{
        root: {
          flexGrow: 0,
          flexShrink: 1,
          width: token.controlHeightLG * 7,
          minWidth: token.controlHeightLG * 3,
          minHeight: 0,
        },
      }}
    />
  );
};

export default HomePageSearchBar;
